import { useCallback, useEffect, useRef, useState } from 'react';

import { notifications } from '@mantine/notifications';
import saveAs from 'file-saver';
import JSZip from 'jszip';

import { useAppSelector } from '@/store/hooks';
import {
  useCreateBatchDownloadMutation,
  useGetBatchDownloadQuery,
  useUpdateBatchDownloadPdfInfoMutation
} from '@/store/slices/api/batchDownloadSlice';
import { useGetNotDownloadedReportsQuery } from '@/store/slices/api/patient/patientListApiSlice';
import { getCurrentDate, getEncodedDateTimeWithTimezone } from '@/utils/constants';

import { BatchJobStatus } from '@/types/batchDownload';
import { LabelAndValue } from '@/types/custom';

const POLLING_INTERVAL = 2500;
const FILE_NAME_PREFIX = 'All_UnDownloaded_Reports';
const BATCH_SIZE = 10;
const MAX_RETRIES = 2;

export interface BatchDownloadState {
  status: BatchJobStatus;
  errorMessage: string;
  showModal: boolean;
  isDownloading: boolean;
  currentBatch: number;
  totalBatches: number;
  downloadProgress: number;
  completedBatches: number;
  failedBatches: number;
  retriedBatches: number;
  completedFiles: string[]; // Store S3 URLs for download
}

export interface EffectiveFilters {
  clinics: LabelAndValue[];
  practitioners: LabelAndValue[];
  deviceTypes: LabelAndValue[];
  vendors: LabelAndValue[];
  patientStatuses: LabelAndValue[];
  currentReportFilters?: {
    colors?: string[];
    isNonPractitionerSigned?: true;
    isPendingReview?: true;
  };
  previousReportFilters?: {
    colors?: string[];
    isRecentSigned?: true;
    recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE';
    isDownloaded?: true;
  };
}

export const useNotDownloadedReports = (
  selectedReportIds?: number[],
  onDownloadComplete?: () => void,
  patientListRefetch?: (readFromWriter?: boolean) => void,
  effectiveFilters?: EffectiveFilters
) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<number | null>(null);
  const [_, setBatchQueue] = useState<
    Array<{ batch: number[]; index: number; retryCount: number }>
  >([]);
  const [currentBatchInfo, setCurrentBatchInfo] = useState<{
    batch: number[];
    index: number;
    retryCount: number;
  } | null>(null);

  // Add cancellation flag
  const isCancelledRef = useRef(false);
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [batchState, setBatchState] = useState<BatchDownloadState>({
    status: BatchJobStatus.INITIALIZED,
    errorMessage: '',
    showModal: false,
    isDownloading: false,
    currentBatch: 0,
    completedBatches: 0,
    totalBatches: 0,
    downloadProgress: 0,
    failedBatches: 0,
    retriedBatches: 0,
    completedFiles: []
  });

  const [allProcessedIds, setAllProcessedIds] = useState<number[]>([]);

  const { filters: globalFilter } = useAppSelector((state) => state.preferences);
  const { previousReportFilters, currentReportFilters } = useAppSelector(
    (state) => state.patientListFilters
  );

  const hasPreviousFilters = previousReportFilters && Object.keys(previousReportFilters).length > 0;
  const hasCurrentFilters = currentReportFilters && Object.keys(currentReportFilters).length > 0;

  // Use effectiveFilters if provided (matches exactly what the list API receives),
  // otherwise fall back to globalFilter from Redux
  const queryClinicIds = effectiveFilters
    ? effectiveFilters.clinics.map((c) => c.value)
    : (globalFilter?.clinics?.map((c) => c.value) ?? undefined);
  const queryPractitionerIds = effectiveFilters
    ? effectiveFilters.practitioners.map((p) => p.value)
    : (globalFilter?.practitioners?.map((p) => p.value) ?? undefined);
  const queryDeviceTypes = effectiveFilters
    ? effectiveFilters.deviceTypes.map((d) => d.value)
    : (globalFilter?.deviceTypes?.map((d) => d.value) ?? undefined);
  const queryVendors = effectiveFilters
    ? effectiveFilters.vendors.map((v) => v.value)
    : (globalFilter?.vendors?.map((v) => v.value) ?? undefined);
  const queryPatientStatuses = effectiveFilters
    ? effectiveFilters.patientStatuses.map((s) => s.value)
    : (globalFilter?.patientStatuses?.map((s) => s.value) ?? undefined);
  const queryCurrentReportFilters = effectiveFilters
    ? effectiveFilters.currentReportFilters
    : hasCurrentFilters && !hasPreviousFilters
      ? currentReportFilters
      : undefined;
  const queryPreviousReportFilters = effectiveFilters
    ? effectiveFilters.previousReportFilters
    : hasPreviousFilters && !hasCurrentFilters
      ? previousReportFilters
      : undefined;

  const {
    data: notDownloadedData,
    isLoading: isNotDownloadedLoading,
    refetch: refetchNotDownloaded
  } = useGetNotDownloadedReportsQuery({
    clinicIds: queryClinicIds,
    practitionersIds: queryPractitionerIds,
    deviceTypes: queryDeviceTypes,
    vendors: queryVendors,
    patientStatuses: queryPatientStatuses,
    previousReportFilters: queryPreviousReportFilters,
    currentReportFilters: queryCurrentReportFilters
  });

  const [createBatchDownload] = useCreateBatchDownloadMutation();
  const [updateBatchDownloadPdfInfo] = useUpdateBatchDownloadPdfInfoMutation();

  // Poll current job if exists - but only if not cancelled
  const { data: currentBatchData } = useGetBatchDownloadQuery(currentJobId!, {
    skip: !currentJobId || isCancelledRef.current,
    pollingInterval: currentJobId && !isCancelledRef.current ? POLLING_INTERVAL : 0
  });

  const notDownloadedCount = notDownloadedData?.length || 0;

  // Helper function to chunk array into batches
  const chunkArray = <T>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, []);

  // Handle polling result
  useEffect(() => {
    // If cancelled, ignore any polling results
    if (isCancelledRef.current) return;

    if (currentBatchData?.batchDownload && currentBatchInfo) {
      const { status, s3SignedUrl } = currentBatchData.batchDownload;

      if (status === BatchJobStatus.SUCCESS && s3SignedUrl) {
        // Add to completed files
        setBatchState((prev) => ({
          ...prev,
          completedFiles: [...prev.completedFiles, s3SignedUrl],
          currentBatch: prev.currentBatch + 1,
          completedBatches: prev.completedBatches + 1,
          downloadProgress: ((prev.currentBatch + 1) / prev.totalBatches) * 100
        }));
        setAllProcessedIds((prev) => [...prev, ...currentBatchInfo.batch]);

        // Clear current job
        setCurrentJobId(null);
        setCurrentBatchInfo(null);

        // Process next batch ONLY after this one is complete and not cancelled
        processingTimeoutRef.current = setTimeout(() => {
          if (!isCancelledRef.current) {
            processNextBatch();
          }
        }, 500);
      } else if (status === BatchJobStatus.FAILED) {
        handleBatchFailure();
      }
      // If status is still PROCESSING, do nothing - keep polling
    }
  }, [currentBatchData]);

  // Handle batch failure with retry logic
  const handleBatchFailure = useCallback(() => {
    if (!currentBatchInfo || isCancelledRef.current) return;

    if (currentBatchInfo.retryCount < MAX_RETRIES) {
      // Add back to front of queue with incremented retry count
      const retryBatch = {
        ...currentBatchInfo,
        retryCount: currentBatchInfo.retryCount + 1
      };

      setBatchQueue((prev) => {
        return [retryBatch, ...prev];
      });
      setBatchState((prev) => ({ ...prev, retriedBatches: prev.retriedBatches + 1 }));
    } else {
      setBatchState((prev) => ({
        ...prev,
        failedBatches: prev.failedBatches + 1,
        currentBatch: prev.currentBatch + 1,
        // Don't increment completedBatches for failed batches
        downloadProgress: ((prev.currentBatch + 1) / prev.totalBatches) * 100
      }));
    }

    // Clear current job and process next batch
    setCurrentJobId(null);
    setCurrentBatchInfo(null);

    // Process next batch after state updates
    processingTimeoutRef.current = setTimeout(() => {
      if (!isCancelledRef.current) {
        processNextBatch();
      }
    }, 500);
  }, [currentBatchInfo]);

  const processNextBatch = useCallback(() => {
    // Check if cancelled before processing
    if (isCancelledRef.current) return;

    setBatchQueue((currentQueue) => {
      if (currentQueue.length === 0 || isCancelledRef.current) {
        setBatchState((prev) => {
          const hasFailures = prev.failedBatches > 0;
          return {
            ...prev,
            status: hasFailures ? BatchJobStatus.FAILED : BatchJobStatus.SUCCESS,
            downloadProgress: 100,
            errorMessage: hasFailures ? `${prev.failedBatches} batches failed permanently` : ''
          };
        });
        setIsDownloading(false);
        return currentQueue;
      }

      // Get next batch
      const nextBatch = currentQueue[0];
      const remainingQueue = currentQueue.slice(1);

      setCurrentBatchInfo(nextBatch);

      // Create batch
      createBatchDownload({
        transmissiondata: nextBatch.batch.map((id) => ({ transmissionId: id }))
      })
        .then((response) => {
          // Check if cancelled after async operation
          if (isCancelledRef.current) return;

          if (response.data?.jobId) {
            // Polling
            setCurrentJobId(response.data.jobId);
          } else {
            // No Job Id.
            handleBatchFailure();
          }
        })
        .catch(() => {
          // Check if cancelled after async operation
          if (isCancelledRef.current) return;
          handleBatchFailure();
        });

      return remainingQueue; // Update queue
    });
  }, [createBatchDownload, handleBatchFailure]);

  const showNotification = (type: 'success' | 'error', message: string, title?: string) => {
    notifications.show({
      title: title || (type === 'success' ? 'Success' : 'Error'),
      message,
      color: type === 'success' ? 'green' : 'red',
      autoClose: 4000
    });
  };

  const cancelProcess = useCallback(() => {
    // Set cancellation flag
    isCancelledRef.current = true;

    // Clear any pending timeouts
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }

    // Reset all states
    setBatchQueue([]);
    setCurrentJobId(null);
    setCurrentBatchInfo(null);
    setAllProcessedIds([]);
    setIsDownloading(false);

    // Update batch state to cancelled
    setBatchState((prev) => ({
      ...prev,
      status: BatchJobStatus.FAILED,
      errorMessage: 'Download cancelled by user',
      downloadProgress: 0
    }));
  }, []);

  const handleClose = useCallback(() => {
    // If currently processing, cancel the process
    if (batchState.status === BatchJobStatus.PROCESSING) {
      cancelProcess();
    }

    // Close modal and reset state
    setBatchState((prev) => ({
      ...prev,
      showModal: false,
      status: BatchJobStatus.INITIALIZED,
      errorMessage: '',
      downloadProgress: 0,
      currentBatch: 0,
      completedBatches: 0,
      totalBatches: 0,
      failedBatches: 0,
      retriedBatches: 0,
      completedFiles: []
    }));

    // Reset cancellation flag for next time
    isCancelledRef.current = false;
  }, [batchState.status, cancelProcess]);

  const onDownloadAllNotDownloadedReports = async () => {
    const hasSelectedReports = selectedReportIds && selectedReportIds.length > 0;
    if ((!hasSelectedReports && notDownloadedCount === 0) || isDownloading || !notDownloadedData)
      return;

    // Reset cancellation flag when starting new process
    isCancelledRef.current = false;

    // Prepare batches
    const transmissionIds =
      selectedReportIds && selectedReportIds.length > 0
        ? selectedReportIds
        : notDownloadedData.map((id) => parseInt(id.toString()));
    const batches = chunkArray(transmissionIds, BATCH_SIZE);

    const queue = batches.map((batch, index) => ({
      batch,
      index,
      retryCount: 0
    }));

    // Reset state and start
    setBatchState({
      status: BatchJobStatus.PROCESSING,
      errorMessage: '',
      showModal: true,
      isDownloading: false,
      currentBatch: 0,
      completedBatches: 0,
      totalBatches: batches.length,
      downloadProgress: 0,
      failedBatches: 0,
      retriedBatches: 0,
      completedFiles: []
    });

    setBatchQueue(queue);
    setCurrentJobId(null);
    setCurrentBatchInfo(null);
    setAllProcessedIds([]); // Reset processed IDs
    setIsDownloading(true);
    // Start first batch with a longer delay to ensure state is set
    processingTimeoutRef.current = setTimeout(() => {
      if (!isCancelledRef.current) {
        processNextBatch();
      }
    }, 500);
  };

  const handleBatchDownload = async () => {
    if (batchState.completedFiles.length === 0) return;

    setBatchState((prev) => ({ ...prev, isDownloading: true }));

    try {
      // Create a new JSZip instance for the master ZIP
      const masterZip = new JSZip();

      // Download all completed files and extract their contents
      const downloadPromises = batchState.completedFiles.map(async (s3Url, index) => {
        const response = await fetch(s3Url);
        const blob = await response.blob();

        // Load the batch ZIP to extract individual PDFs
        const batchZip = await JSZip.loadAsync(blob);

        // Extract each PDF from the batch ZIP and add directly to master ZIP root
        const pdfPromises = Object.keys(batchZip.files).map(async (filename) => {
          const file = batchZip.files[filename];
          if (!file.dir && filename.endsWith('.pdf')) {
            const pdfBlob = await file.async('blob');

            // Add PDF directly to root of master ZIP (no folders)
            masterZip.file(filename, pdfBlob);
          }
        });

        await Promise.all(pdfPromises);
        return { success: true, index };
      });

      await Promise.all(downloadPromises);

      // Generate the master ZIP file
      const masterZipBlob = await masterZip.generateAsync({ type: 'blob' });

      // Save the master ZIP file
      const masterFileName = `${FILE_NAME_PREFIX}_${getCurrentDate('MM.DD.YYYY')}.zip`;
      saveAs(masterZipBlob, masterFileName);

      // Update PDF info with all successfully processed report IDs
      await updateBatchDownloadPdfInfo({
        tranmissionReportIds: allProcessedIds,
        downloadDate: getEncodedDateTimeWithTimezone()
      });

      // Refetch not-downloaded reports to update the count
      refetchNotDownloaded();

      const failedCount = batchState.failedBatches;

      const message =
        failedCount > 0
          ? `${notDownloadedCount} reports downloaded successfully, (${failedCount * BATCH_SIZE} reports failed)`
          : `${notDownloadedCount} reports downloaded successfully`;

      showNotification(failedCount > 0 ? 'error' : 'success', message);

      // Reset
      setBatchState({
        status: BatchJobStatus.INITIALIZED,
        errorMessage: '',
        showModal: false,
        isDownloading: false,
        currentBatch: 0,
        completedBatches: 0,
        totalBatches: 0,
        downloadProgress: 0,
        failedBatches: 0,
        retriedBatches: 0,
        completedFiles: []
      });

      onDownloadComplete?.();
      setAllProcessedIds([]); // Reset processed IDs
      setIsDownloading(false);
      // The download mutation just committed on the writer. Request this one
      // refresh from the writer rather than waiting for Aurora replica lag.
      patientListRefetch?.(true);
    } catch (error) {
      setBatchState((prev) => ({
        ...prev,
        status: BatchJobStatus.FAILED,
        errorMessage: error instanceof Error ? error.message : 'Download failed',
        isDownloading: false
      }));
    }
  };

  return {
    notDownloadedCount,
    isNotDownloadedLoading,
    isDownloading,
    batchState,
    setBatchState,
    onDownloadAllNotDownloadedReports,
    handleBatchDownload,
    handleClose,
    cancelProcess
  };
};
